import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { OwnershipScope } from '../types';

/**
 * Custom hook to check user scopes and permissions
 *
 * Provides utilities to check if a user has specific scopes
 * and can access resource management features.
 */
export function useUserScopes() {
  const user = useSelector((state: RootState) => state.auth.user);
  const scopes = user?.scopes || [];
  const role = user?.role;

  /**
   * Check if user has a specific scope
   */
  const hasScope = (scope: OwnershipScope): boolean => {
    return scopes.includes(scope);
  };

  /**
   * Check if user can access Teams
   *
   * Logic:
   * - IS_OWNER: NO access (only see Programs, not Teams)
   * - PMO/ADMIN/EXECUTIVE: YES (full access)
   * - PROGRAM_LEAD/DIVISION_LEAD/TEAM_LEAD with scopes: YES (scoped access)
   * - Others: NO access
   */
  const canAccessTeams = (): boolean => {
    // IS_OWNER explicitly forbidden from Teams
    if (role === 'IS_OWNER') {
      return false;
    }

    // PMO, ADMIN, EXECUTIVE have full access
    if (role === 'PMO' || role === 'ADMIN' || role === 'EXECUTIVE') {
      return true;
    }

    // Users with relevant scopes can access
    if (
      hasScope(OwnershipScope.PROGRAM_LEAD_SCOPE) ||
      hasScope(OwnershipScope.DIVISION_LEAD_SCOPE) ||
      hasScope(OwnershipScope.TEAM_LEAD_SCOPE)
    ) {
      return true;
    }

    // Default: no access
    return false;
  };

  /**
   * Check if user can access resource management pages
   *
   * Logic:
   * - IS_OWNER: NO access
   * - PMO/ADMIN/EXECUTIVE: YES (full access)
   * - PROGRAM_LEAD with PROGRAM_LEAD_SCOPE: YES (scoped access)
   * - DIVISION_LEAD_SCOPE: YES (scoped access)
   * - Others: NO access
   */
  const canAccessResourceManagement = (): boolean => {
    // IS_OWNER explicitly forbidden
    if (role === 'IS_OWNER') {
      return false;
    }

    // PMO, ADMIN, EXECUTIVE have full access
    if (role === 'PMO' || role === 'ADMIN' || role === 'EXECUTIVE') {
      return true;
    }

    // PROGRAM_LEAD with PROGRAM_LEAD_SCOPE can access
    if (role === 'PROGRAM_LEAD' && hasScope(OwnershipScope.PROGRAM_LEAD_SCOPE)) {
      return true;
    }

    // Anyone with DIVISION_LEAD_SCOPE can access
    if (hasScope(OwnershipScope.DIVISION_LEAD_SCOPE)) {
      return true;
    }

    // Default: no access
    return false;
  };

  /**
   * Check if user can access Scenarios
   *
   * Logic:
   * - IS_OWNER: NO access to scenarios
   * - PMO/ADMIN/EXECUTIVE: YES (full access, all programs)
   * - PROGRAM_LEAD with PROGRAM_LEAD_SCOPE: YES (scoped to led programs)
   * - Others: NO access
   */
  const canAccessScenarios = (): boolean => {
    if (role === 'IS_OWNER') {
      return false;
    }

    if (role === 'PMO' || role === 'ADMIN' || role === 'EXECUTIVE') {
      return true;
    }

    if (role === 'PROGRAM_LEAD' && hasScope(OwnershipScope.PROGRAM_LEAD_SCOPE)) {
      return true;
    }

    return false;
  };

  return {
    scopes,
    hasScope,
    canAccessTeams,
    canAccessResourceManagement,
    canAccessScenarios,
    // Convenience flags
    isIsOwner: hasScope(OwnershipScope.IS_OWNER_SCOPE),
    isProgramLead: hasScope(OwnershipScope.PROGRAM_LEAD_SCOPE),
    isDivisionLead: hasScope(OwnershipScope.DIVISION_LEAD_SCOPE),
    isTeamLead: hasScope(OwnershipScope.TEAM_LEAD_SCOPE),
  };
}
